import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "famfeel_family_token";
const FAMILY_ID_KEY = "famfeel_family_id";
const FAMILY_NAME_KEY = "famfeel_family_name";

export const auth = {
  getToken: () => localStorage.getItem(TOKEN_KEY),
  getFamilyId: () => localStorage.getItem(FAMILY_ID_KEY),
  getFamilyName: () => localStorage.getItem(FAMILY_NAME_KEY),
  setSession: ({ token, family_id, name }) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (family_id) localStorage.setItem(FAMILY_ID_KEY, family_id);
    if (name) localStorage.setItem(FAMILY_NAME_KEY, name);
  },
  clear: () => {
    const fid = localStorage.getItem(FAMILY_ID_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(FAMILY_ID_KEY);
    localStorage.removeItem(FAMILY_NAME_KEY);
    localStorage.removeItem("famfeel_viewer_id");
    // Private-note encryption key is tied to (pin, family_id); drop it on sign-out.
    if (fid) localStorage.removeItem("famfeel_pnk_" + fid);
  },
};

const client = axios.create({ baseURL: API });

client.interceptors.request.use((config) => {
  const token = auth.getToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  // House-lock: attach the `X-House-Unlocks` header on every request so
  // list endpoints filter content to houses we've unlocked, and write
  // endpoints can skip the per-request `house_pin` plumbing for simple
  // reads. The value is a base64-encoded JSON dict of {house_id: pin},
  // built from sessionStorage. Empty map omitted to keep headers small.
  try {
    const raw = sessionStorage.getItem("famfeel_house_unlocks_v1");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && Object.keys(parsed).length) {
        const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(parsed))));
        config.headers = config.headers || {};
        config.headers["X-House-Unlocks"] = encoded;
      }
    }
  } catch {
    /* noop — sessionStorage unavailable (Safari private mode, etc.) */
  }
  return config;
});

client.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      // Don't clear auth for endpoints where a 401 means "wrong PIN guess",
      // not "your session is invalid". Wrong-PIN 401s on these endpoints
      // should stay on the dialog and let the user retry. Currently:
      //   - /family/verify-pin — parent-PIN gate for Settings, exit from
      //     Toddler mode, etc. A toddler mashing the keypad must not
      //     boot the device back to the join screen.
      //   - /houses/{id}/verify-pin — same reasoning for per-house PINs.
      const url = err?.config?.url || "";
      const isPinGuess =
        /\/family\/verify-pin$/.test(url) ||
        /\/houses\/[^/]+\/verify-pin$/.test(url);
      if (!isPinGuess) {
        auth.clear();
        if (typeof window !== "undefined" && !window.__famfeel_redirecting) {
          window.__famfeel_redirecting = true;
          window.location.reload();
        }
      }
    }
    return Promise.reject(err);
  }
);

export const api = {
  // family auth (public)
  createFamily: (data) =>
    axios.post(`${API}/families/create`, data).then((r) => r.data),
  joinFamily: (data) =>
    axios.post(`${API}/families/join`, data).then((r) => r.data),

  // family (protected)
  getFamilySettings: () => client.get("/family/settings").then((r) => r.data),
  updateFamilySettings: (data) =>
    client.put("/family/settings", data).then((r) => r.data),
  rotateJoinCode: () => client.post("/family/rotate-code").then((r) => r.data),
  signOutOthers: (data) => client.post("/family/sign-out-others", data).then((r) => r.data),
  signOutThisDevice: () => client.post("/family/sign-out-this-device").then((r) => r.data),
  listSessions: () => client.get("/family/sessions").then((r) => r.data),
  kickSession: (sid) => client.delete(`/family/sessions/${sid}`).then((r) => r.data),
  deleteFamily: () => client.delete("/family").then((r) => r.data),
  verifyFamilyPin: (pin) => client.post("/family/verify-pin", { pin }).then((r) => r.data),
  rotateFamilyPin: (current_pin, new_pin) =>
    client.post("/family/rotate-pin", { current_pin, new_pin }).then((r) => r.data),

  // bring-your-own Google AI key (powers the flyer scanner)
  setByoGoogleKey: (key) =>
    client.post("/family/byo-google-key", { key }).then((r) => r.data),
  clearByoGoogleKey: () =>
    client.delete("/family/byo-google-key").then((r) => r.data),

  // flyer scanner — extract events from a photo
  scanFlyerImage: ({ image, language_hint }) =>
    client.post("/events/from-image", { image, language_hint }).then((r) => r.data),

  // feedback — "send the makers a note"
  sendFeedback: ({ message, member_id, contact_email }) =>
    client.post("/feedback", { message, member_id, contact_email }).then((r) => r.data),

  // landing analytics (no auth)
  pingVisit: (data) =>
    axios.post(`${API}/analytics/visit`, data).then((r) => r.data),

  // admin
  adminLogin: (password) =>
    axios.post(`${API}/admin/login`, { password }).then((r) => r.data),
  adminStats: (token) =>
    axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.data),
  adminFamilyLookup: (token, joinCode) =>
    axios.get(`${API}/admin/family/${encodeURIComponent(joinCode.toUpperCase())}`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.data),
  adminFamiliesList: (token) =>
    axios.get(`${API}/admin/families`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.data),
  adminOverrideTier: (token, { join_code, tier, note }) =>
    axios.post(`${API}/admin/family/override-tier`, { join_code, tier, note }, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => r.data),
  adminDeleteFamily: (token, joinCode) => {
    const code = joinCode.toUpperCase();
    return axios
      .delete(`${API}/admin/family/${encodeURIComponent(code)}`, {
        params: { confirm: code },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => r.data);
  },
  adminFeedbackList: (token, status) =>
    axios
      .get(`${API}/admin/feedback`, {
        params: status ? { status } : {},
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => r.data),
  adminFeedbackUpdate: (token, id, status) =>
    axios
      .patch(
        `${API}/admin/feedback/${encodeURIComponent(id)}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((r) => r.data),
  adminForceBirthdayPush: (token, family_id, member_id) =>
    axios
      .post(
        `${API}/admin/push/birthday`,
        member_id ? { family_id, member_id } : { family_id },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((r) => r.data),
  adminInactiveNudge: (token, { min_inactive_days = 3, dry_run = false } = {}) =>
    axios
      .post(
        `${API}/admin/push/inactive-nudge`,
        { min_inactive_days, dry_run },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((r) => r.data),

  // members
  listMembers: () => client.get("/members").then((r) => r.data),
  createMember: (data) => client.post("/members", data).then((r) => r.data),
  updateMember: (id, data) =>
    client.put(`/members/${id}`, data).then((r) => r.data),
  reorderMembers: (ordered_ids) =>
    client.put("/members/reorder", { ordered_ids }).then((r) => r.data),
  deleteMember: (id, opts = {}) =>
    client
      .delete(`/members/${id}`, { params: opts.house_pin ? { house_pin: opts.house_pin } : {} })
      .then((r) => r.data),
  verifyPin: (id, pin) =>
    client.post(`/members/${id}/verify-pin`, { pin }).then((r) => r.data),

  // checkins
  todayCheckins: (params) => client.get("/checkins/today", { params }).then((r) => r.data),
  upsertCheckin: (data) => client.post("/checkins", data).then((r) => r.data),
  reactCheckin: (id, data) =>
    client.post(`/checkins/${id}/react`, data).then((r) => r.data),
  markSeen: (id, data) =>
    client.post(`/checkins/${id}/seen`, data).then((r) => r.data),
  history: (memberId, days = 30) =>
    client.get(`/checkins/history/${memberId}`, { params: { days } }).then((r) => r.data),
  familyStreak: () => client.get("/family/streak").then((r) => r.data),
  weekReview: () => client.get("/family/week-review").then((r) => r.data),
  logPetWalk: (data) => client.post("/pets/walk", data).then((r) => r.data),
  setPetNextWalk: (data) => client.post("/pets/next-walk", data).then((r) => r.data),
  logPetCare: (data) => client.post("/pets/care", data).then((r) => r.data),
  logPetCareMilestone: (pet_id, kind) =>
    client.post("/pets/care-log", { pet_id, kind }).then((r) => r.data),
  petHistory: (pet_id, days = 30) =>
    client.get(`/pets/${pet_id}/history`, { params: { days } }).then((r) => r.data),

  // messages
  listMessages: () => client.get("/messages").then((r) => r.data),
  createMessage: (data) => client.post("/messages", data).then((r) => r.data),
  deleteMessage: (id) => client.delete(`/messages/${id}`).then((r) => r.data),
  react: (id, data) => client.post(`/messages/${id}/react`, data).then((r) => r.data),

  // events (family calendar)
  listEvents: (params) => client.get("/events", { params }).then((r) => r.data),
  upcomingEvents: (limit = 20) =>
    client.get("/events/upcoming", { params: { limit } }).then((r) => r.data),
  createEvent: (data) => client.post("/events", data).then((r) => r.data),
  unmergeEvent: (id, member_id) =>
    client.post(`/events/${encodeURIComponent(id)}/unmerge`, { member_id }).then((r) => r.data),
  updateEvent: (id, data) => client.put(`/events/${encodeURIComponent(id)}`, data).then((r) => r.data),
  ackEvent: (id, data) => client.post(`/events/${encodeURIComponent(id)}/ack`, data).then((r) => r.data),
  deleteEvent: (id, opts = {}) => {
    const params = {};
    if (opts.house_pin) params.house_pin = opts.house_pin;
    if (opts.scope) params.scope = opts.scope;
    return client
      .delete(`/events/${encodeURIComponent(id)}`, { params })
      .then((r) => r.data);
  },
  checkEventConflicts: (data) =>
    client.post("/events/check-conflicts", data).then((r) => r.data),
  calendarChanged: (member_id) =>
    client
      .get("/calendar/changed", {
        params: member_id ? { member_id } : {},
      })
      .then((r) => r.data),
  calendarSubscribeInfo: () =>
    client.get("/calendar/subscribe-info").then((r) => r.data),
  calendarSubscribeRegenerate: () =>
    client.post("/calendar/subscribe-info/regenerate").then((r) => r.data),
  calendarSeen: (member_id) =>
    client.post("/calendar/seen", { member_id }).then((r) => r.data),

  // Event templates
  listEventTemplates: () => client.get("/event-templates").then((r) => r.data),
  createEventTemplate: (data) =>
    client.post("/event-templates", data).then((r) => r.data),
  deleteEventTemplate: (id) =>
    client.delete(`/event-templates/${id}`).then((r) => r.data),

  // billing
  billingStatus: () => client.get("/billing/status").then((r) => r.data),
  billingCheckout: (data) => client.post("/billing/checkout", data).then((r) => r.data),
  billingCheckoutStatus: (session_id) =>
    client.get(`/billing/checkout/status/${session_id}`).then((r) => r.data),
  billingCancel: () => client.post("/billing/cancel").then((r) => r.data),
  billingDowngrade: (data) =>
    client.post("/billing/downgrade", data).then((r) => r.data),
  billingResume: () => client.post("/billing/resume").then((r) => r.data),
  billingPortal: (origin_url) =>
    client.post("/billing/portal", { package_id: "noop", origin_url }).then((r) => r.data),

  // web push
  pushVapidKey: () => client.get("/push/vapid-key").then((r) => r.data),
  pushSubscribe: (data) => client.post("/push/subscribe", data).then((r) => r.data),
  pushUnsubscribe: (data) => client.post("/push/unsubscribe", data).then((r) => r.data),
  pushGetNotificationMode: (endpoint) =>
    client
      .get("/push/notification-mode", { params: { endpoint } })
      .then((r) => r.data),
  pushSetNotificationMode: (endpoint, notification_mode) =>
    client
      .post("/push/notification-mode", { endpoint, notification_mode })
      .then((r) => r.data),
  pushGetAudience: (endpoint) =>
    client.get("/push/audience", { params: { endpoint } }).then((r) => r.data),
  pushSetAudience: (endpoint, body) =>
    client.post("/push/audience", { endpoint, ...body }).then((r) => r.data),
  pushNudge: (data) => client.post("/push/nudge", data).then((r) => r.data),
  pushTest: () => client.post("/push/test").then((r) => r.data),
  pushPreviewEvent: (memberId) =>
    client.get("/push/preview-event", { params: { member_id: memberId } }).then((r) => r.data),
  pushCleanupInactive: () =>
    client.post("/push/cleanup-inactive").then((r) => r.data),

  // houses (Extended Family tier)
  listHouses: () => client.get("/houses").then((r) => r.data),
  createHouse: (data) => client.post("/houses", data).then((r) => r.data),
  updateHouse: (id, data) => client.patch(`/houses/${id}`, data).then((r) => r.data),
  deleteHouse: (id, { pin } = {}) =>
    client
      .delete(`/houses/${id}`, pin ? { headers: { "X-House-Pin": pin } } : undefined)
      .then((r) => r.data),
  requestDeleteHouse: (id, requesterName) =>
    client
      .post(`/houses/${id}/request-delete`, null, {
        params: requesterName ? { requester_name: requesterName } : undefined,
      })
      .then((r) => r.data),
  cancelDeleteHouse: (id) =>
    client.post(`/houses/${id}/cancel-delete`).then((r) => r.data),
  setHousePin: (id, data) =>
    client.post(`/houses/${id}/set-pin`, data).then((r) => r.data),

  // Calendar subscriptions (read-only ICS feeds, e.g. Google Calendar)
  listCalendarSubscriptions: () =>
    client.get("/calendar/subscriptions").then((r) => r.data),
  createCalendarSubscription: (data) =>
    client.post("/calendar/subscriptions", data).then((r) => r.data),
  deleteCalendarSubscription: (id) =>
    client.delete(`/calendar/subscriptions/${id}`).then((r) => r.data),
  syncCalendarSubscription: (id) =>
    client.post(`/calendar/subscriptions/${id}/sync`).then((r) => r.data),
  verifyHousePin: (id, pin) =>
    client.post(`/houses/${id}/verify-pin`, { pin }).then((r) => r.data),

  // chores
  listChores: () => client.get("/chores").then((r) => r.data),
  reorderChores: (order) =>
    client.post("/chores/reorder", { order }).then((r) => r.data),
  createChore: (data) => client.post("/chores", data).then((r) => r.data),
  updateChore: (id, data) => client.put(`/chores/${id}`, data).then((r) => r.data),
  deleteChore: (id) => client.delete(`/chores/${id}`).then((r) => r.data),
  completeChore: (id, data) => client.post(`/chores/${id}/complete`, data).then((r) => r.data),

  // todo lists (Cozi-style shared lists w/ public share token)
  listTodoLists: () => client.get("/todo-lists").then((r) => r.data),
  createTodoList: (data) => client.post("/todo-lists", data).then((r) => r.data),
  reorderTodoLists: (ordered_ids) =>
    client.put("/todo-lists/reorder", { ordered_ids }).then((r) => r.data),
  updateTodoList: (id, data) => client.patch(`/todo-lists/${id}`, data).then((r) => r.data),
  linkTodoListToEvent: (id, event_id) =>
    client.post(`/todo-lists/${id}/link-event`, { event_id }).then((r) => r.data),
  deleteTodoList: (id) => client.delete(`/todo-lists/${id}`).then((r) => r.data),
  todosByMember: () => client.get("/todo-lists/by-member").then((r) => r.data),
  listTodoItems: (listId) =>
    client.get(`/todo-lists/${listId}/items`).then((r) => r.data),
  createTodoItem: (listId, data) =>
    client.post(`/todo-lists/${listId}/items`, data).then((r) => r.data),
  reorderTodoItems: (listId, ordered_ids) =>
    client.put(`/todo-lists/${listId}/items/reorder`, { ordered_ids }).then((r) => r.data),
  updateTodoItem: (listId, itemId, data) =>
    client.patch(`/todo-lists/${listId}/items/${itemId}`, data).then((r) => r.data),
  deleteTodoItem: (listId, itemId) =>
    client.delete(`/todo-lists/${listId}/items/${itemId}`).then((r) => r.data),
  // public share endpoints (no auth)
  todoShareView: (token) =>
    axios.get(`${API}/todo-share/${token}`).then((r) => r.data),
  todoShareCheck: (token, item_id, done) =>
    axios.post(`${API}/todo-share/${token}/check`, { item_id, done }).then((r) => r.data),
};
